import React, { useEffect, useState } from 'react';
import { MapPin, Navigation, Phone, Clock } from 'lucide-react';
import { fetchCMSContent } from '../lib/cms';
import { motion } from 'framer-motion';

const Locations = () => {
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    const loadLocations = async () => {
      const data = await fetchCMSContent('locations');
      setLocations(data);
    };
    loadLocations();
  }, []);

  return (
    <section id="lokationer" className="py-24 bg-brand-light/30 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-black mb-4">To lokationer på Djursland</h2>
          <p className="text-xl text-zinc-600 max-w-2xl mx-auto">
            Nem afhentning tæt på dig. Vi står klar til at hjælpe dig i både Grenå og Glesborg.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {locations.map((loc, index) => (
            <motion.div 
              key={loc.id} 
              initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="bg-white rounded-none overflow-hidden shadow-xl border border-zinc-100 group"
            >
              <a 
                href={loc.map_url}
                target="_blank"
                rel="noopener noreferrer"
                className="h-64 relative overflow-hidden block"
              >
                <img
                  src={loc.image[0]}
                  alt={loc.name.da}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500 flex items-center justify-center">
                  <div className="bg-white/90 p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 shadow-lg">
                    <Navigation size={24} className="text-black" />
                  </div>
                </div>
              </a>
              <div className="p-8">
                <h3 className="text-2xl font-black uppercase text-black mb-6">{loc.name.da}</h3>
                
                <div className="space-y-4 mb-8">
                  <a 
                    href={loc.map_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 text-zinc-600 hover:text-black transition-colors group/address"
                  >
                    <MapPin className="text-black shrink-0 mt-1 group-hover/address:scale-110 transition-transform" size={20} />
                    <div>
                      <p className="font-semibold text-zinc-800 group-hover/address:underline">{loc.address}</p>
                      <p>{loc.zip} {loc.city}</p>
                    </div>
                  </a>
                  <div className="flex items-start gap-3 text-zinc-600">
                    <Phone className="text-black shrink-0 mt-1" size={20} />
                    <a href={`tel:${loc.phone}`} className="hover:text-black transition-colors">{loc.phone}</a>
                  </div>
                  <div className="flex items-start gap-3 text-zinc-600">
                    <Clock className="text-black shrink-0 mt-1" size={20} />
                    <div dangerouslySetInnerHTML={{ __html: loc.opening_hours.da }} className="prose prose-sm prose-zinc" />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={loc.map_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 bg-black text-white px-6 py-3 rounded-none font-bold hover:bg-zinc-800 transition-all"
                  >
                    <Navigation size={18} />
                    Få vejvisning
                  </a>
                  <a
                    href={`tel:${loc.phone}`}
                    className="flex-1 flex items-center justify-center gap-2 border-2 border-black text-black px-6 py-3 rounded-none font-bold hover:bg-zinc-50 transition-all"
                  >
                    Kontakt os
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Locations;
